<template>
  <v-container fluid>

    <!-- Title -->
    <Title :title="fullname" :description="description"/>

    <!-- User info -->
    <v-row v-if="user">

      <!-- Personal info -->
      <v-col cols="12">
        <v-card outlined :class="isEditing ? 'edit-border-px' : ''">
          <v-card-title>
            <v-icon>mdi-account</v-icon>
            <span class="mx-2">{{ isEditing ? 'Editando información personal' : 'Información personal' }}</span>
            <v-btn
                :color="isEditing ? 'primary' : ''"
                icon
                @click="handleEditMode"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <v-form ref="formEditUser" :readonly="!isEditing">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                      outlined
                      dense
                      label="Nombre *"
                      v-model="user.name"
                      :rules="[rules.required, rules.counter]"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                      outlined
                      dense
                      label="Nombre de usuario *"
                      v-model="user.newUsername"
                      :rules="[rules.required, rules.usernameCounter]"
                      counter
                      maxlength="20"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                      outlined
                      dense
                      label="Teléfono *"
                      v-model="user.phone"
                      :rules="[rules.required, rules.phone]"
                      type="text"
                      counter
                      maxlength="12"
                      @input="formatPhone"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                      outlined
                      dense
                      label="Correo electrónico *"
                      v-model="user.email"
                      :rules="[rules.required, rules.email]"
                      type="email"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu
                      v-model="birthDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                      :disabled="!isEditing"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                          v-model="formattedBirthdate"
                          label="Fecha de nacimiento *"
                          prepend-inner-icon="mdi-calendar-outline"
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                          required
                          :rules="[rules.required]"
                          :hint="currentAgeDisplay"
                          persistent-hint
                      />
                    </template>
                    <v-date-picker
                        v-model="user.birthdate"
                        locale="es-mx"
                        :active-picker.sync="birthDateActivePicker"
                        :max="new Date(Date.now() - 1000 * 60 * 60 * 24 * 365 * 18).toISOString().substr(0, 10)"
                        :min="new Date(Date.now() - 1000 * 60 * 60 * 24 * 365 * 100).toISOString().substr(0, 10)"
                        @change="saveBirthDate"
                    >

                    </v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-expand-transition>
            <div v-if="isEditing">
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <CancelButton
                    @click="handleEditMode"
                    :isDisabled="isLoading"
                />
                <SaveButton
                    :isLoading="isLoading"
                    :isDisabled="!isValidForm()"
                    @click="editUser"
                />
              </v-card-actions>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>

      <!-- User registration info -->
      <v-col cols="12" lg="7">
        <v-card class="fill-height" outlined>
          <!-- Title -->
          <v-card-title>
            <v-icon>mdi-file-document</v-icon>
            <span class="mx-2">Información de registro</span>
            <v-switch
                :color="user.status
                  ? 'success'
                  : 'secondary lighten-3'"
                v-model="user.status"
                :loading="isLoadingStatus"
                @click="handleStatusChange"
                hide-details
                class="mt-0 pt-0"
            >
              <template v-slot:label>
                <StatusChip :status="user.status"/>
              </template>
            </v-switch>
          </v-card-title>

          <v-divider></v-divider>

          <!-- Content -->
          <v-card-text>
            <v-row>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title>Fecha de registro</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDate(user.createdAt) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title>Registrado por</v-list-item-title>
                    <v-list-item-subtitle>{{ user.createdBy }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title>Última actualización</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDate(user.updatedAt) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title>Actualizado por</v-list-item-title>
                    <v-list-item-subtitle>{{ user.updatedBy ? user.updatedBy : 'N/A' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
              <v-col cols="6" md="4">
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title>Último inicio de sesión</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDateAndTime(user.lastLogin) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-content>
                    <v-list-item-title>Fecha de renovación de contrato</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDate(user.renovationDate) }}</v-list-item-subtitle>
                    <v-list-item-action class="mx-0">
                      <v-btn
                          @click="openDialogRenovation"
                          color="primary"
                          small
                      >
                        <v-icon left>mdi-calendar-edit</v-icon>
                        Renovar contrato
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item-content>
                  <DialogRenovation
                      v-model="isRenovationDialogOpen"
                      :username="user.username"
                      @updated="getUserDetails"
                  />
                </v-list-item>
              </v-col>
            </v-row>
          </v-card-text>

          <DialogConfirmation ref="dialogConfirmation" @ok="changeStatus"
                              @cancel="revertStatusChange"
                              :confirmationTitle="originalStatus ? `¿Deshabilitar a ${user.name}?` : `¿Habilitar a ${user.name}?`"
                              :message="'¿Estás seguro de que deseas ' + (originalStatus ? 'deshabilitar' : 'habilitar') + ' a ' + (user.name + ' ' + user.lastname1 + ' ' + user.lastname2) + '?'"
          />

          <DialogConfirmation ref="dialogOfficeChangeConfirmation" @ok="assignDieticianToOffice"
                              @cancel="selectedOffice = null"
                              :confirmationTitle="`¿Asignar a ${user.name} al consultorio ${selectedOffice ? selectedOffice.name : ''}?`"
                              :message="'¿Estás seguro de que deseas asignar a ' + (user.name + ' ' + user.lastname1 + ' ' + user.lastname2) + ' al consultorio ' + (selectedOffice ? selectedOffice.name : '') + '?'"
          />

        </v-card>
      </v-col>

      <!-- Role info -->
      <v-col cols="12" lg="5">

        <!-- Dietician info -->
        <v-card
            v-if="user.role === 'DIETICIAN'"
            outlined
        >
          <v-card-title>
            <v-icon>mdi-account-group</v-icon>
            <span class="ms-2">Información de Dietista</span>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <v-list-item class="px-0">
              <v-list-item-content class="py-0">
                <v-list-item-title>Consultorio</v-list-item-title>
                <v-list-item-subtitle>{{
                    user.officeName ? user.officeName : 'Sin consultorio'
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="ms-0" v-if="isAllowed(PERMISSIONS.OFFICE_ASSIGN)">
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn small color="primary" v-on="on" @click="getAvailableOffices()">
                      <v-icon left>mdi-office-building</v-icon>
                      Asignar consultorio
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item v-if="isLoadingOffices">
                      <v-list-item-content>
                        <v-list-item-title>Cargando consultorios disponibles...</v-list-item-title>
                        <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-else-if="offices.length === 0">
                      <v-list-item-content>
                        <v-list-item-title>No hay consultorios disponibles</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-for="office in offices" :key="office.name"
                                 @click="handleOfficeChange(office)" v-if="offices">
                      <v-list-item-content>
                        <v-list-item-title>{{ office.name }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>

            <v-list-item class="px-0">
              <v-list-item-content class="py-0">
                <v-list-item-title>Saldo actual</v-list-item-title>
                <v-list-item-subtitle v-if="!isLoadingBalance">${{ user.balance }}</v-list-item-subtitle>
                <v-list-item-subtitle v-else>
                  Cargando saldo...
                  <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action v-if="isAllowed(PERMISSIONS.DIETICIAN_BALANCE)">
                <!-- Add balance -->
                <v-btn block small color="primary" @click="openDialogAddBalance">
                  <v-icon left>mdi-cash-plus</v-icon>
                  Agregar saldo
                </v-btn>
                <DialogAddBalance ref="dialogAddBalance" :user="user" @updateBalance="updateBalance()"/>

                <!-- Set balance -->
                <v-btn block small color="green white--text" class="mt-2" @click="openDialogSetBalance">
                  <v-icon left>mdi-cash-edit</v-icon>
                  Establecer saldo
                </v-btn>
                <DialogSetBalance ref="dialogSetBalance" :user="user" @updateBalance="updateBalance()"/>
              </v-list-item-action>
            </v-list-item>

            <v-list-item class="px-0">
              <v-list-item-content class="py-0">
                <v-list-item-title>Regalías del mes anterior</v-list-item-title>
                <v-list-item-subtitle v-if="!isLoadingBalance">
                  ${{ user.royalties.toFixed(2) }} —
                  <v-chip
                      small
                      label
                      :color="royaltiesStatusColor"
                  >
                    {{ royaltiesStatus }}
                  </v-chip>
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else>
                  Cargando regalías...
                  <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action v-if="isAllowed(PERMISSIONS.DIETICIAN_BALANCE)">
                <!-- Change royalties status -->
                <v-btn
                    block
                    small
                    color="amber white--text"
                    @click="openDialogUpdateRoyalties"
                >
                  <v-icon left>mdi-account-cash</v-icon>
                  Cambiar estado de regalías
                </v-btn>
                <DialogUpdateRoyalties
                    ref="dialogUpdateRoyalties"
                    :value="isUpdateRoyaltiesDialogOpen"
                    :username="user.username"
                    :royaltiesStatus="user.royaltiesStatus"
                    @updated="getUserDetails()"
                    @input="isUpdateRoyaltiesDialogOpen = $event"
                    v-if="user.role === 'DIETICIAN'"
                />
              </v-list-item-action>
            </v-list-item>
          </v-card-text>
        </v-card>

        <!-- Admin info -->
        <v-card
            outlined
            v-if="user.role === 'ADMIN'"
            :class="isEditingPermissions ? 'edit-border-px' : ''"
        >
          <v-card-title>
            <v-icon>mdi-account-group</v-icon>
            <span class="mx-2">{{
                isEditingPermissions ? 'Editando permisos de administrador' : 'Permisos de administrador'
              }}</span>
            <v-btn icon :color="isEditingPermissions ? 'primary' : ''" @click="handleEditPermissionsMode()">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <v-virtual-scroll :items="permissions" :item-height="52" height="256">
              <template v-slot="{ item }">
                <v-list-item>
                  <v-list-item-content class="py-0">
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-checkbox
                        :disabled="!isEditingPermissions"
                        :input-value="isPermissionAssigned(item)"
                        @click.stop="togglePermission(item)"
                    />
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-virtual-scroll>
          </v-card-text>

          <v-expand-transition>
            <div v-if="isEditingPermissions">
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <CancelButton :isDisabled="isLoading" @click="handleEditPermissionsMode"/>
                <SaveButton
                    :isLoading="isLoadingPermissions"
                    @click="saveUserPermissions"
                />
              </v-card-actions>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>

      <!-- Return button -->
      <v-col cols="12">
        <ReturnButton v-if="user"/>
      </v-col>

    </v-row>

    <!-- Skeleton loader -->
    <OverlayLoading v-else/>

  </v-container>
</template>

<script lang="ts">
import Vue from "vue";

import UserService from "@/services/users/UserService";
import OfficeService from "@/services/offices/OfficeService";
import PermissionService from "@/services/permissions/PermissionService";

import {RequestSaveUserPermissions} from "@/services/users/entities/RequestSaveUserPermissions";
import throwSuccessMessage from "@/utils/SuccessMessage";
import throwErrorMessage from "@/utils/ErrorMessage";

import {User} from "@/services/users/entities/User";
import {OfficeName} from "@/services/offices/entities/OfficeName";
import {Permission} from "@/services/permissions/entities/Permission";
import {isAllowed} from "@/utils/UniversalFunctions";

import Title from "@/components/Title.vue";
import DialogConfirmation from "@/components/DialogConfirmation.vue";
import DialogAddBalance from "@/views/users/components/DialogAddBalance.vue";
import DialogSetBalance from "@/views/users/components/DialogSetBalance.vue";
import DialogUpdateRoyalties from "@/views/users/components/DialogUpdateRoyalties.vue";
import OverlayLoading from "@/components/OverlayLoading.vue";
import StatusChip from "@/components/StatusChip.vue";
import {PERMISSIONS} from "@/utils/Permissions";
import CancelButton from "@/components/buttons/CancelButton.vue";
import ReturnButton from "@/components/buttons/ReturnButton.vue";
import SaveButton from "@/components/buttons/SaveButton.vue";
import DialogRenovation from "@/views/users/components/DialogRenovation.vue";


export default Vue.extend({
  name: "UserInfo",
  components: {
    DialogRenovation,
    SaveButton,
    ReturnButton,
    CancelButton,
    StatusChip,
    Title,
    DialogConfirmation,
    DialogAddBalance,
    DialogSetBalance,
    DialogUpdateRoyalties,
    OverlayLoading,
  },
  data() {
    return {
      // User info
      user: null as User | null,
      username: '' as string,
      originalUser: null as User | null,
      originalStatus: false as boolean,
      fullname: '' as string,
      description: '' as string,
      royaltiesStatusColor: 'primary' as string,

      // Office info
      offices: [] as OfficeName[],
      selectedOffice: null as OfficeName | null,

      // Permissions info
      permissions: [] as Permission[],
      userPermissions: [] as Permission[],
      originalUserPermissions: [] as Permission[],

      // Loading and editing
      isEditing: false as boolean,
      isEditingPermissions: false as boolean,
      isLoading: false as boolean,
      isLoadingStatus: false as boolean,
      isLoadingOffices: false as boolean,
      isLoadingBalance: false as boolean,
      isLoadingPermissions: false as boolean,
      isLoadingRoyalties: false as boolean,

      // Dialog
      isRenovationDialogOpen: false as boolean,
      isUpdateRoyaltiesDialogOpen: false as boolean,

      // Validation
      rules: {
        required: (value: string) => !!value || 'Campo requerido',
        counter: (value: string) => (value || '').length <= 50 || 'Máximo 50 caracteres',
        usernameCounter: (value: string) => (value || '').length <= 20 || 'Máximo 20 caracteres',
        email: (value: string) => {
          const pattern = /^[a-zA-Z0-9ñÑ._%+-]+@[a-zA-Z0-9ñÑ.-]+.[a-zA-ZñÑ]{2,}$/;
          return pattern.test(value) || 'Correo electrónico inválido';
        },
        phone: (value: string) => {
          const pattern = /^\d{3}-\d{3}-\d{4}$/;
          return pattern.test(value) || 'Teléfono inválido';
        },
      },

      // Birthdate picker
      birthDateMenu: false,
      birthDateActivePicker: 'MONTH' as 'YEAR' | 'MONTH' | 'DATE',

      // Renovation date picker
      renovationDateMenu: false,
      renovationDateActivePicker: 'MONTH' as 'YEAR' | 'MONTH' | 'DATE',
    };
  },

  watch: {
    birthDateMenu(val) {
      val && setTimeout(() => (this.birthDateActivePicker = 'YEAR'));
    },
  },

  computed: {
    PERMISSIONS() {
      return PERMISSIONS
    },
    formattedBirthdate(): string | null {
      if (!this.user) return null;
      const [year, month, day] = this.user.birthdate.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      return new Intl.DateTimeFormat('es-MX', options).format(date);
    },
    currentAgeDisplay(): string | null {
      if (!this.user) return null;
      const birthDate = new Date(this.user.birthdate);
      const today = new Date();

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      return `${age} años`;
    },
    royaltiesStatus(): string {
      switch (this.user?.royaltiesStatus) {
        case 'PENDING':
          this.royaltiesStatusColor = 'yellow';
          return 'Pendiente de pago';
        case 'PAID':
          this.royaltiesStatusColor = 'success';
          return 'Pagado';
        case 'EXEMPT':
          this.royaltiesStatusColor = 'secondary';
          return 'Exento de pago';
        case 'NOT_APPLICABLE':
          this.royaltiesStatusColor = 'secondary';
          return 'No aplica';
        default:
          return 'Desconocido';
      }
    }
  },

  methods: {
    handleEditMode() {
      if (this.isEditing) {
        this.isEditing = false;
        this.user = this.originalUser;
      } else {
        this.isEditing = true;
        this.originalUser = {...this.user} as User;
      }
    },
    handleEditPermissionsMode() {
      if (this.isEditingPermissions) {
        this.isEditingPermissions = false;
        this.userPermissions = this.originalUserPermissions;
      } else {
        this.isEditingPermissions = true;
        this.originalUserPermissions = [...this.userPermissions];
      }
    },
    isPermissionAssigned(permission: Permission) {
      return this.userPermissions.some(p => p.name === permission.name);
    },
    togglePermission(permission: Permission) {
      if (this.isPermissionAssigned(permission)) {
        this.userPermissions = this.userPermissions.filter(
            perm => perm.name !== permission.name && perm.parentName !== permission.name
        );
      } else {
        let updated = [...this.userPermissions];
        if (permission.parentName && !this.userPermissions.some(
            perm => perm.name === permission.parentName
        )) {
          const parent = this.permissions.find(p => p.name === permission.parentName);
          if (parent) {
            updated.push(parent);
          }
        }
        updated.push(permission);
        this.userPermissions = updated;
      }
    },
    handleStatusChange() {
      this.originalStatus = !this.user?.status as boolean;
      (this.$refs.dialogConfirmation as any).dialog = true;
    },
    handleOfficeChange(office: OfficeName) {
      this.selectedOffice = office;
      (this.$refs.dialogOfficeChangeConfirmation as any).dialog = true;
    },
    async getUserDetails() {
      this.isLoading = true;
      try {
        this.user = await UserService.getUserByUsername(this.username);
        this.user!.newUsername = this.user!.username;
        this.fullname = `${this.user!.name} ${this.user!.lastname1} ${this.user!.lastname2}`;
      } finally {
        this.isLoading = false;
      }
    },
    async changeStatus() {
      this.isLoadingStatus = true;
      this.originalStatus = this.user!.status;

      try {
        await UserService.changeUserStatus(this.user!.username);

        this.$swal.fire({
          toast: true,
          icon: 'success',
          titleText: '¡Éxito!',
          text: `${this.user!.name} ${this.user!.status ? 'habilitado' : 'deshabilitado'} exitosamente.`,
          showConfirmButton: false,
          timer: 4000,
          allowEscapeKey: true,
          background: '#fff',
          color: '#000',
          iconColor: '#ff6600',
          timerProgressBar: true,
          position: 'top-end',
          target: '.v-application',
        });
      } finally {
        this.isLoadingStatus = false;
      }
    },
    async revertStatusChange() {
      this.user!.status = this.originalStatus;
      this.isLoadingStatus = false;
    },
    async getAvailableOffices() {
      if (this.offices.length > 0) return;
      this.isLoadingOffices = true;
      try {
        this.offices = await OfficeService.getAvailableOffices();
      } finally {
        this.isLoadingOffices = false;
      }
    },
    async assignDieticianToOffice() {
      if (!this.user) return;
      if (!this.selectedOffice) return;

      this.isLoading = true;
      try {
        const body = {
          username: this.user.username,
          officeId: this.selectedOffice!.id,
        }
        await UserService.assignDieticianToOffice(body);
        this.user.officeName = this.selectedOffice.name;
        this.description = `Dietista de ${this.selectedOffice.name}`;

        throwSuccessMessage(`${this.user.name} asignado a ${this.selectedOffice.name} exitosamente.`)
      } finally {
        this.isLoading = false;
      }
    },
    async updateBalance() {
      this.isLoadingBalance = true;
      try {
        const response = await UserService.getBalance(this.username);
        this.user!.balance = response.balance;
      } finally {
        this.isLoadingBalance = false;
      }
    },
    async getUserPermissions() {
      this.isLoadingPermissions = true;
      try {
        this.permissions = await PermissionService.getAllPermissions();
        this.userPermissions = await UserService.getUserPermissions(this.username);
      } finally {
        this.isLoadingPermissions = false;
      }
    },
    async saveUserPermissions() {
      this.isLoadingPermissions = true;
      try {
        const body = {
          username: this.username,
          permissions: this.userPermissions.map(p => ({name: p.name})),
        } as RequestSaveUserPermissions;
        await UserService.saveUserPermissions(body);
        this.originalUserPermissions = [...this.userPermissions];
        this.isEditingPermissions = false;

        throwSuccessMessage('Permisos actualizados exitosamente.');

        await this.getUserPermissions();
      } finally {
        this.isLoadingPermissions = false;
      }
    },
    async editUser() {
      const form = this.$refs.formEditUser as any;
      if (form.validate()) {
        try {
          this.isLoading = true;
          const body = {
            name: this.user!.name,
            lastname1: this.user!.lastname1,
            lastname2: this.user!.lastname2,
            username: this.user!.username,
            newUsername: this.user!.newUsername,
            phone: this.user!.phone,
            email: this.user!.email,
            birthdate: this.user!.birthdate,
          }
          await UserService.editUser(body);
          this.isEditing = false;
          this.originalUser = {...this.user} as User;
          this.username = this.user!.newUsername as string;
          this.fullname = `${this.user!.name} ${this.user!.lastname1} ${this.user!.lastname2}`;
          throwSuccessMessage("Usuario actualizado exitosamente.");
        } finally {
          this.isLoading = false;
        }
      } else {
        throwErrorMessage("Asegúrate de llenar todos los campos correctamente");
      }
    },
    isValidForm() {
      return !(!this.user?.name ||
          !this.user?.lastname1 ||
          !this.user?.username ||
          !this.user?.phone ||
          !this.user?.birthdate);
    },
    isAllowed(permissionName: string) {
      return isAllowed(permissionName);
    },
    saveBirthDate() {
      this.birthDateMenu = false;
    },
    textRole(role: string) {
      switch (role) {
        case 'SUPERADMIN':
          return 'Administrador General'
        case 'ADMIN':
          return 'Administrador'
        case 'DIETICIAN':
          return 'Dietista'
        default:
          return ''
      }
    },
    formatDate(date: string) {
      if (!date) return 'N/A';
      const [year, month, day] = date.split('T')[0].split('-').map(Number);
      const formattedDate = new Date(year, month - 1, day);
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      return new Intl.DateTimeFormat('es-MX', options).format(formattedDate);
    },
    formatDateAndTime(date: string) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    },
    formatPhone(value: string) {
      if (!value) return;
      if (!this.user) return;
      let digits = value.replace(/\D/g, '');

      if (digits.length > 10) {
        digits = digits.substring(0, 10);
      }

      if (digits.length > 6) {
        this.user.phone = digits.replace(/(\d{3})(\d{3})(\d{0,4})/, '$1-$2-$3');
      } else if (digits.length > 3) {
        this.user.phone = digits.replace(/(\d{3})(\d{0,3})/, '$1-$2');
      } else {
        this.user.phone = digits;
      }
    },
    openDialogAddBalance() {
      (this.$refs.dialogAddBalance as any).dialog = true;
    },
    openDialogSetBalance() {
      (this.$refs.dialogSetBalance as any).dialog = true;
    },
    openDialogRenovation() {
      this.isRenovationDialogOpen = true;
    },
    openDialogUpdateRoyalties() {
      this.isUpdateRoyaltiesDialogOpen = true;
    },
  },

  async created() {
    this.username = this.$route.params.username;
    this.user = await UserService.getUserByUsername(this.username);

    if (!this.user) {
      this.$router.go(-1);
      return;
    }

    this.user.newUsername = this.user.username;
    this.fullname = `${this.user.name} ${this.user.lastname1} ${this.user.lastname2}`;

    if (this.user.role === 'ADMIN') {
      this.description = 'Administrador de DIETEC SDR';
      await this.getUserPermissions();
    } else if (this.user.role === 'DIETICIAN') {
      this.description = this.user.officeName
          ? `Dietista de DIETEC ${this.user.officeName}`
          : 'Dietista sin consultorio';
    }
  },
});
</script>

<style scoped>
.edit-border-px {
  border: 1px solid #ff6600 !important;
  transition: border 0.5s !important;
}
</style>
